// Sync engine (spec §6.4). Push pending ops, apply results, pull since
// last_seq, merge with server rows winning, then re-apply still-pending ops so
// optimistic state survives until acked. Never blocks the UI.
import { createStore } from 'zustand/vanilla'
import { AuthError, NetworkError, type ApiClient } from './api'
import { applyOpLocally, migrateLocal } from './ops'
import { META, type Db } from './schema'
import type { AgentNote, Edge, Op, OpRecord, Resource } from './types'
import type { KindRegistry } from '@/kinds/kind'
import { nowISO, todayLocal } from '@/lib/time'
import { memberMaySend } from '@/state/access'

export type SyncStatus = 'synced' | 'pending' | 'offline' | 'syncing' | 'paused' | 'error'

export interface SyncState {
  status: SyncStatus
  /** the server's event stream is open: changes from other devices arrive within a second */
  live: boolean
  pendingCount: number
  /** the records with a change this device has not had answered yet (rows mark them while offline) */
  unsent: string[]
  lastSyncAt: string | null
  lastError: string | null
  serverToday: string | null
  serverTz: string | null
}

const PURGE_AFTER_DAYS = 30
/**
 * Kinds removed from both registries. The server deleted their rows without a
 * seq (migration 00009), so a device that pulled them earlier would keep them
 * forever; it drops them here. Only named kinds, never "whatever this bundle
 * does not know": a newer server may send a kind an older bundle has not met.
 */
export const RETIRED_KINDS = ['PlaneIssue']

export interface SyncOptions {
  /** debounce after a local op */
  debounceMs?: number
  /** periodic sync while visible */
  intervalMs?: number
  /** navigator/window are absent in tests */
  attachListeners?: boolean
}

/** The ids the open ops touch, sorted; the same array back when nothing changed (no re-render). */
export const unsentIds = (open: object[], was: string[]): string[] => {
  const idOf = (o: { id?: string; resource?: { id?: string } }) => o.id ?? o.resource?.id
  const ids = [...new Set(open.map((o) => idOf(o)).filter((x): x is string => !!x))].sort()
  return ids.length === was.length && ids.every((x, i) => x === was[i]) ? was : ids
}

export class SyncEngine {
  /** live agent notes, set by the app so the sync layer stays free of UI state */
  onAgentNote?: (n: AgentNote) => void

  readonly store = createStore<SyncState>(() => ({
    status: 'synced',
    live: false,
    pendingCount: 0,
    unsent: [],
    lastSyncAt: null,
    lastError: null,
    serverToday: null,
    serverTz: null,
  }))
  private timer: ReturnType<typeof setTimeout> | null = null
  private interval: ReturnType<typeof setInterval> | null = null
  private running: Promise<void> | null = null
  private rerun = false
  private fullPullRequested = false
  private paused = false

  constructor(
    private db: Db,
    private api: ApiClient,
    private kinds: KindRegistry,
    private opts: SyncOptions = {},
  ) {}

  /** Attach browser triggers: online, visibility, interval. */
  start() {
    if (this.opts.attachListeners === false || typeof window === 'undefined') return
    window.addEventListener('online', () => void this.sync())
    window.addEventListener('offline', () => this.set({ status: 'offline' }))
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.sync()
        this.connectEvents()
      } else this.disconnectEvents()
    })
    window.addEventListener('online', () => this.connectEvents())
    this.connectEvents()
    this.interval = setInterval(() => {
      if (document.visibilityState === 'visible') void this.sync()
    }, this.opts.intervalMs ?? 60_000)
    navigator.serviceWorker?.addEventListener('message', (ev) => {
      if (ev.data?.type === 'sync') void this.sync()
    })
  }

  stop() {
    this.disconnectEvents()
    if (this.interval) clearInterval(this.interval)
    if (this.timer) clearTimeout(this.timer)
  }

  /** After an auth fix, resume. */
  resume() {
    this.paused = false
    void this.sync()
  }

  private set(patch: Partial<SyncState>) {
    this.store.setState(patch)
  }

  /** Debounced trigger used after every local op. */
  schedule() {
    void this.refreshPending()
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => void this.sync(), this.opts.debounceMs ?? 500)
  }

  private async refreshPending() {
    const open = await this.db.ops.where('state').anyOf('pending', 'sent').toArray()
    this.set({ pendingCount: open.length, unsent: unsentIds(open, this.store.getState().unsent) })
    if (open.length > 0 && this.store.getState().status === 'synced') this.set({ status: 'pending' })
  }

  private events: AbortController | null = null
  private eventsBackoff = 1000

  /**
   * Hold the server's event stream open while the app is visible. Every commit
   * on the server arrives as a seq; if it is past what this device has pulled,
   * sync now instead of at the next poll. Reconnects with backoff; the poll
   * stays as the fallback so nothing depends on the stream.
   */
  connectEvents() {
    if (this.events || typeof document === 'undefined' || document.visibilityState !== 'visible') return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return
    const ctl = new AbortController()
    this.events = ctl
    void (async () => {
      try {
        const bootstrapped = await this.db.getMeta<string>(META.bootstrappedAt)
        if (!bootstrapped || ctl.signal.aborted) return
        await this.api.events(
          async (seq) => {
            this.eventsBackoff = 1000
            if (!this.store.getState().live) this.set({ live: true })
            const have = (await this.db.getMeta<number>(META.lastSeq)) ?? 0
            if (seq > have) void this.sync()
          },
          ctl.signal,
          this.onAgentNote,
        )
      } catch (e) {
        if (e instanceof AuthError) return // sync() will surface it
      } finally {
        if (this.events === ctl) this.events = null
        if (this.store.getState().live) this.set({ live: false })
        if (!ctl.signal.aborted) {
          // dropped: try again, slower each time up to half a minute
          const wait = this.eventsBackoff
          this.eventsBackoff = Math.min(this.eventsBackoff * 2, 30_000)
          setTimeout(() => this.connectEvents(), wait)
        }
      }
    })()
  }

  disconnectEvents() {
    this.events?.abort()
    this.events = null
    if (this.store.getState().live) this.set({ live: false })
  }

  /** Single-flight sync; a call during a run queues one more run. */
  sync(): Promise<void> {
    if (this.running) {
      this.rerun = true
      return this.running
    }
    this.running = this.run().finally(() => {
      this.running = null
      if (this.rerun) {
        this.rerun = false
        void this.sync()
      }
    })
    return this.running
  }

  private async run(): Promise<void> {
    if (this.paused) return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.set({ status: 'offline' })
      return
    }
    const bootstrapped = await this.db.getMeta<string>(META.bootstrappedAt)
    if (!bootstrapped) return
    this.set({ status: 'syncing' })
    try {
      await this.push()
      await this.pull()
      await this.purgeDeleted()
      const open = await this.db.ops.where('state').anyOf('pending', 'sent').toArray()
      const pending = open.length
      this.set({
        status: pending ? 'pending' : 'synced',
        pendingCount: pending,
        unsent: unsentIds(open, this.store.getState().unsent),
        lastSyncAt: nowISO(),
        lastError: null,
      })
    } catch (e) {
      if (e instanceof AuthError) {
        this.paused = true
        this.set({ status: 'paused', lastError: 'Server rejected the token' })
        return
      }
      if (e instanceof NetworkError) {
        this.set({ status: 'offline', lastError: e.message })
        return
      }
      this.set({ status: 'error', lastError: (e as Error).message })
      throw e
    }
  }

  /** First load: pull everything and register the device. */
  async bootstrap(deviceName?: string): Promise<void> {
    const deviceId = await this.ensureDeviceId()
    const boot = await this.api.bootstrap()
    await this.db.transaction('rw', this.db.resources, this.db.edges, this.db.meta, async () => {
      await this.db.resources.clear()
      await this.db.edges.clear()
      await this.db.resources.bulkPut(boot.resources.map((r) => migrateLocal(this.kinds, r)))
      await this.db.edges.bulkPut(boot.edges)
      await this.db.setMeta(META.lastSeq, boot.seq)
      await this.db.setMeta(META.bootstrappedAt, nowISO())
    })
    await this.reapplyPending()
    try {
      await this.api.registerDevice({ id: deviceId, ...(deviceName ? { name: deviceName } : {}) })
      const now = await this.api.now()
      await this.db.setMeta(META.serverTz, now.tz)
      this.set({ serverTz: now.tz, serverToday: now.today })
    } catch {
      /* device registration is best-effort */
    }
    this.set({ status: 'synced', lastSyncAt: nowISO() })
  }

  async ensureDeviceId(): Promise<string> {
    let id = await this.db.getMeta<string>(META.deviceId)
    if (!id) {
      const { v7 } = await import('uuid')
      id = v7()
      await this.db.setMeta(META.deviceId, id)
    }
    return id
  }

  private async push(): Promise<void> {
    let pending = await this.db.ops.where('state').anyOf('pending', 'sent').sortBy('created_at')
    if (!pending.length) return
    if (await this.db.getMeta<boolean>(META.member)) {
      // a member's Settings and the like stay on this device: the server would
      // refuse them, so they are not sent (and not left looking unsynced)
      const local = pending.filter((o) => !memberMaySend(o))
      if (local.length) await this.db.ops.bulkPut(local.map((o) => ({ ...o, state: 'acked' as const })))
      pending = pending.filter((o) => memberMaySend(o))
      if (!pending.length) return
    }
    for (let i = 0; i < pending.length; i += 200) {
      const batch = pending.slice(i, i + 200)
      await this.db.ops.bulkPut(batch.map((o) => ({ ...o, state: 'sent' as const })))
      const resp = await this.api.push(batch.map(stripRecord))
      await this.db.transaction(
        'rw',
        this.db.resources,
        this.db.edges,
        this.db.ops,
        this.db.meta,
        async () => {
          for (const res of resp.results) {
            const rec = batch.find((o) => o.op_id === res.op_id)
            if (!rec) continue
            if (res.status === 'rejected') {
              await this.db.ops.put({
                ...rec,
                state: 'rejected',
                error: res.error ?? { code: 'unknown', message: 'rejected' },
              })
              // our optimistic state may be wrong now; a full pull rebuilds it
              this.fullPullRequested = true
              continue
            }
            await this.db.ops.put({ ...rec, state: 'acked', acked_at: new Date().toISOString() })
            if (res.resource) await this.db.resources.put(migrateLocal(this.kinds, res.resource))
          }
        },
      )
    }
    // ops that stayed 'sent' (missing from results) go back to pending
    await this.db.ops.where('state').equals('sent').modify({ state: 'pending' })
  }

  private async pull(): Promise<void> {
    let since = this.fullPullRequested ? 0 : ((await this.db.getMeta<number>(META.lastSeq)) ?? 0)
    this.fullPullRequested = false
    for (;;) {
      const page = await this.api.pull(since)
      await this.merge(page.resources, page.edges, page.seq)
      since = page.seq
      if (!page.more) break
    }
    await this.reapplyPending()
    try {
      const now = await this.api.now()
      this.set({ serverToday: now.today, serverTz: now.tz })
      await this.db.setMeta(META.serverTz, now.tz)
      await this.db.setMeta(META.serverToday, now.today)
    } catch {
      /* non-essential */
    }
  }

  private async merge(resources: Resource[], edges: Edge[], seq: number): Promise<void> {
    await this.db.transaction('rw', this.db.resources, this.db.edges, this.db.meta, async () => {
      if (resources.length) await this.db.resources.bulkPut(resources.map((r) => migrateLocal(this.kinds, r)))
      if (edges.length) await this.db.edges.bulkPut(edges)
      await this.db.setMeta(META.lastSeq, seq)
    })
  }

  /** Re-apply un-acked ops over server rows so optimistic state survives. */
  private async reapplyPending(): Promise<void> {
    const pending = await this.db.ops.where('state').anyOf('pending', 'sent').sortBy('created_at')
    if (!pending.length) return
    await this.db.transaction('rw', this.db.resources, this.db.edges, async () => {
      for (const op of pending) await applyOpLocally(this.db, this.kinds, stripRecord(op))
    })
  }

  private async purgeDeleted(): Promise<void> {
    const cutoff = new Date(Date.now() - PURGE_AFTER_DAYS * 86_400_000).toISOString()
    await this.db.resources
      .where('deleted_at')
      .below(cutoff)
      .delete()
      .catch(() => {})
    const retired = await this.db.resources.where('kind').anyOf(RETIRED_KINDS).primaryKeys()
    if (retired.length) {
      await this.db.transaction('rw', this.db.resources, this.db.edges, async () => {
        await this.db.resources.bulkDelete(retired)
        await this.db.edges.where('from_id').anyOf(retired).delete()
        await this.db.edges.where('to_id').anyOf(retired).delete()
      })
    }
    const acked = await this.db.ops
      .where('state')
      .equals('acked')
      .and((o) => o.created_at < cutoff)
      .primaryKeys()
    if (acked.length) await this.db.ops.bulkDelete(acked)
  }

  /** Retry a rejected op (re-queue) or discard it. */
  async retryOp(opId: string, discard = false): Promise<void> {
    const rec = await this.db.ops.get(opId)
    if (!rec) return
    if (discard) await this.db.ops.delete(opId)
    else {
      const { v7 } = await import('uuid')
      await this.db.ops.delete(opId)
      await this.db.ops.put({ ...rec, op_id: v7(), state: 'pending', error: undefined })
      await this.reapplyPending()
    }
    this.schedule()
  }

  /** Compare server "today" with the device's. */
  dayMismatch(): { server: string; local: string } | null {
    const s = this.store.getState().serverToday
    const l = todayLocal()
    return s && s !== l ? { server: s, local: l } : null
  }
}

const stripRecord = (rec: OpRecord): Op => {
  const {
    created_at: _c,
    state: _s,
    error: _e,
    summary: _m,
    before: _b,
    group: _g,
    undo_of: _u,
    undone_at: _ua,
    no_undo: _n,
    acked_at: _aa,
    ...op
  } = rec
  return op as Op
}
